import React, { useCallback, useContext } from "react";

import { PickerDate } from "@euroland/calendar-react";
import { Button } from "@euroland/base-ui";

import Label from "../ui/Label";
import NumberInput from "../ui/NumberInput";
import Controller from "../Form/Controller";
import { classNames } from "../../utils";
import { useCompanySettings, useFormatSetting, useSelectedInstrument } from "../../hooks/useCompanySettings";
import QuadrataSquareCheckbox from "../ui/Checkbox/QuadrataSquareCheckbox";
import i18n from "../../locale/i18n";
import { useBreakpoint } from "../../hooks/useResize";
import { SharesPurchasedSelectContext } from "../../context/AmountAndShareSelectContext";
import withSharesPurchasedSelection from "../../hocs/withSharesPurchasedSelectio";
import { isUseLatinNumber } from "../../store/utils";
import { SHORT_DATE_FORMAT, TAB } from "../../constant/common";
import { Dropdown, Option } from "@euroland/dropdown-react";
import useCurrencyName from "../../hooks/useCurrencyName";

const QuadrataSharesPurchasedSelection = ({
  onSubmit,
  enabledDividendOption,
  loading,
  minStartDate,
  maxStartDate,
  minEndDate,
  maxEndDate,
}) => {
  const companySettings = useCompanySettings();
  const { isMobileScreen } = useBreakpoint();
  const format = useFormatSetting();

  const formState = useContext(SharesPurchasedSelectContext);
  const { setFieldValue, getFieldValue, tab } = formState;

  const [, { formatNumber }] = useSelectedInstrument();
  const getCurrencyNameByCode = useCurrencyName();

  const handleFormatNumber = useCallback(value => formatNumber(value, { decimalDigits: 0 }), [formatNumber]);
  const calendarFormat = format.calendarDate || format.shortDate || SHORT_DATE_FORMAT;

  // const renderNumberInput = useCallback(
  //   ({ error, ref, name, title, errorMessage }) => {
  //     return (
  //       <NumberInput
  //         useLatinNumbers={isUseLatinNumber}
  //         className={classNames({ error: !!error?.[name] }, "quadrata-selection__input")}
  //         ref={ref}
  //         value={getFieldValue(name)}
  //         onChange={value => setFieldValue(name, value)}
  //         formatNumber={handleFormatNumber}
  //         title={title}
  //         id={name}
  //         errorMessage={errorMessage}
  //       />
  //     );
  //   },
  //   [getFieldValue, setFieldValue, handleFormatNumber, companySettings.useLatinNumber]
  // );
  const isRtl = window.appSettings.isRtl;

  return (
    <>
      <div className="quadrata-selection__wrapper">
        <div className="quadrata-selection__row">
          <div className=" quadrata-selection__item">
            <Label
              htmlFor="dateOfInvestmentPurchase"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.SHARES_PURCHASED })}
            >
              {i18n.translate("dateOfInvestment")}
            </Label>
            <PickerDate
              id="dateOfInvestmentPurchase"
              dateFormat={calendarFormat}
              className={classNames("quadrata-selection__input", {
                left: isRtl,
                right: !isRtl,
              })}
              value={getFieldValue("startDate")}
              align={isMobileScreen ? "right" : "left"}
              minDate={minStartDate}
              maxDate={maxStartDate}
              onChange={date => setFieldValue("startDate", date)}
              textCancel={i18n.translate("cancel")}
              textApply={i18n.translate("apply")}
            />
          </div>
          <div className="quadrata-selection__item">
            <Label
              htmlFor="endDateOfInvestmentPurchase"
              className={classNames({ "d-hidden-accessibility": tab !== TAB.SHARES_PURCHASED })}
            >
              {i18n.translate("endDateOfInvestment")}
            </Label>
            <PickerDate
              id="endDateOfInvestmentPurchase"
              dateFormat={calendarFormat}
              align="right"
              className={classNames("quadrata-selection__input", {
                left: isRtl,
                right: !isRtl,
              })}
              value={getFieldValue("endDate")}
              minDate={minEndDate}
              maxDate={maxEndDate}
              onChange={date => setFieldValue("endDate", date)}
              textCancel={i18n.translate("cancel")}
              textApply={i18n.translate("apply")}
            />
          </div>
        </div>
        <div
          className="quadrata-selection__row"
          style={companySettings.currencyEnabled ? undefined : { rowGap: 0 }}
        >
          <div className="quadrata-selection__item">
            <Label htmlFor="shareNumber">{i18n.translate("numberOfSharesInvested")}</Label>
            {/* <Controller
              formState={formState}
              name="shareNumber"
              render={({ ref, error }) =>
                renderNumberInput({
                  ref,
                  error,
                  name: "shareNumber",
                  title: i18n.translate("numberOfSharesInvested"),
                  errorMessage: error?.["shareNumber"] ? i18n.translate("shareNumberInputRequired") : ''
                })
              }
            /> */}
            <NumberInput
              useLatinNumbers={isUseLatinNumber}
              className={classNames({ error: !!formState.error?.["shareNumber"] }, "quadrata-selection__input")}
              value={getFieldValue("shareNumber")}
              onChange={value => setFieldValue("shareNumber", value)}
              formatNumber={handleFormatNumber}
              title={i18n.translate("numberOfSharesInvested")}
              id={"shareNumber"}
              errorMessage={formState.error?.["shareNumber"] ? i18n.translate("fieldRequired") : ''}
            />
          </div>
          <div className="quadrata-selection__item">
            {companySettings.currencyEnabled && tab === TAB.SHARES_PURCHASED && (
              <>
                <Label>{i18n.translate("chooseCurrency")}</Label>
                <Dropdown
                  icon={<span className="fs-triangle-down fs-icon"></span>}
                  value={getFieldValue("currency")}
                  className="quadrata-selection__input"
                  placeholder={i18n.translate("localCurrency")}
                  onOptionSelect={value => setFieldValue("currency", value)}
                  aria-label={i18n.translate("chooseCurrency")}
                  dropdownMenuLabel="dropdown currency"
                >
                  <Option value="">{i18n.translate("localCurrency")}</Option>
                  {companySettings.currencies.map(({ code, text }) => (
                    <Option key={code} value={code}  aria-label={getCurrencyNameByCode(code)}>
                      {text || code}
                    </Option>
                  ))}
                </Dropdown>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="quadrata-selection__action">
        {enabledDividendOption && tab === TAB.SHARES_PURCHASED && (
          <QuadrataSquareCheckbox
            className="quadrata-selection__ratio"
            selected={getFieldValue("dividendChecked")}
            onSelect={() => setFieldValue("dividendChecked", !getFieldValue("dividendChecked"))}
          />
        )}

        <Button
          outline
          className="quadrata-selection__submit button--outline"
          onClick={onSubmit}
          loading={loading}
        >
          <span className="button-text">{i18n.translate("showData")}</span>
          <span className="fs-icon fs-right-dash-arrow"></span>
        </Button>
      </div>
    </>
  );
};

export default withSharesPurchasedSelection(QuadrataSharesPurchasedSelection);
